import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ShieldCheck,
  ArrowLeft,
  Clock,
  User,
  Building,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react'
import {
  useAttestationForReview,
  useApproveAttestation,
  useRejectAttestation,
  useRequestAttestationChanges,
} from '@/hooks/useAdminAttestations'
import AttestationReviewActions from '@/components/employee/AttestationReviewActions'

// Framework display info
const FRAMEWORK_INFO: Record<string, { name: string; fullName: string; product: string; color: string }> = {
  hipaa: {
    name: 'HIPAA',
    fullName: 'Health Insurance Portability and Accountability Act',
    product: 'Clinical',
    color: 'text-red-600 bg-red-100',
  },
  eeoc: {
    name: 'EEOC',
    fullName: 'Equal Employment Opportunity Commission',
    product: 'Talent',
    color: 'text-blue-600 bg-blue-100',
  },
  batson: {
    name: 'Batson',
    fullName: 'Batson v. Kentucky Compliance',
    product: 'Legal',
    color: 'text-purple-600 bg-purple-100',
  },
  ferpa: {
    name: 'FERPA',
    fullName: 'Family Educational Rights and Privacy Act',
    product: 'Education',
    color: 'text-green-600 bg-green-100',
  },
  finra: {
    name: 'FINRA',
    fullName: 'Financial Industry Regulatory Authority',
    product: 'Finance',
    color: 'text-amber-600 bg-amber-100',
  },
}

export default function AttestationReview() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showConfirmModal, setShowConfirmModal] = useState<'approve' | 'reject' | 'changes' | null>(null)
  const [actionNotes, setActionNotes] = useState('')
  const [actionInProgress, setActionInProgress] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const { data: attestation, isLoading, error } = useAttestationForReview(id || null)

  const approveMutation = useApproveAttestation()
  const rejectMutation = useRejectAttestation()
  const requestChangesMutation = useRequestAttestationChanges()

  const frameworkInfo = attestation ? FRAMEWORK_INFO[attestation.framework] : null

  const handleAction = async (action: 'approve' | 'reject' | 'changes') => {
    if (!attestation) return

    setActionError(null)
    setActionInProgress(true)

    try {
      switch (action) {
        case 'approve':
          await approveMutation.mutateAsync({ id: attestation.id, notes: actionNotes || undefined })
          break
        case 'reject':
          await rejectMutation.mutateAsync({ id: attestation.id, notes: actionNotes })
          break
        case 'changes':
          await requestChangesMutation.mutateAsync({ id: attestation.id, notes: actionNotes })
          break
      }
      // Success - navigate back to queue
      navigate('/employee/attestations')
    } catch (err: any) {
      setActionError(err.response?.data?.detail || `Failed to ${action} attestation`)
    } finally {
      setActionInProgress(false)
      setShowConfirmModal(null)
      setActionNotes('')
    }
  }

  const openActionModal = (action: 'approve' | 'reject' | 'changes') => {
    setActionNotes('')
    setActionError(null)
    setShowConfirmModal(action)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (error || !attestation) {
    return (
      <div className="text-center py-12">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Attestation Not Found</h2>
        <p className="text-gray-600 mb-4">The attestation you're looking for doesn't exist or has been removed.</p>
        <Link
          to="/employee/attestations"
          className="inline-flex items-center gap-2 text-personic-600 hover:text-personic-700"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Queue
        </Link>
      </div>
    )
  }

  const isPriority = attestation.days_pending > 3

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Back Link */}
      <Link
        to="/employee/attestations"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Queue
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${frameworkInfo?.color || 'bg-gray-100'}`}>
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {frameworkInfo?.name || attestation.framework.toUpperCase()} Attestation
              </h1>
              {isPriority && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                  <AlertTriangle className="w-3 h-3" />
                  Priority
                </span>
              )}
            </div>
            <p className="text-gray-500 mt-1">{frameworkInfo?.fullName}</p>
          </div>
        </div>

        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
          <Clock className="w-4 h-4" />
          Pending Review
        </span>
      </div>

      {/* Action Error */}
      {actionError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{actionError}</p>
        </div>
      )}

      {/* Tenant Context Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Building className="w-5 h-5 text-gray-400" />
          Tenant Information
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Organization</p>
            <p className="font-medium text-gray-900">{attestation.tenant_name || 'Unknown'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Plan</p>
            <p className="font-medium text-gray-900 capitalize">{attestation.tenant_plan || 'Unknown'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Product</p>
            <p className="font-medium text-gray-900">{frameworkInfo?.product}</p>
          </div>
        </div>
      </div>

      {/* Attestor Information Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-gray-400" />
          Attestor Information
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Name</p>
            <p className="font-medium text-gray-900">{attestation.attested_by_name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Title</p>
            <p className="font-medium text-gray-900">{attestation.attested_by_title}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Email</p>
            <p className="font-medium text-gray-900">{attestation.attested_by_email || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">IP Address</p>
            <p className="font-medium text-gray-900 font-mono text-sm">{attestation.ip_address}</p>
          </div>
        </div>
      </div>

      {/* Submission Details Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-400" />
          Submission Details
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Submitted</p>
            <p className="font-medium text-gray-900">
              {new Date(attestation.submitted_at).toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Days Pending</p>
            <p className={`font-medium ${isPriority ? 'text-amber-600' : 'text-gray-900'}`}>
              {attestation.days_pending} days
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Expires</p>
            <p className="font-medium text-gray-900">
              {new Date(attestation.expires_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Acknowledgments Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-400" />
          Acknowledgments
        </h2>
        <div className="space-y-3">
          {Object.entries(attestation.attestation_data?.acknowledgments || {}).map(([key, value]) => (
            <div
              key={key}
              className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
            >
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 capitalize">
                  {key.replace(/_/g, ' ')}
                </p>
                <p className="text-sm text-gray-600">
                  {typeof value === 'boolean' ? (value ? 'Acknowledged' : 'Not acknowledged') : String(value)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Signature Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Signature</h2>
        <div className="p-4 bg-gray-50 rounded-lg">
          {attestation.signature_type === 'drawn' && attestation.signature_data ? (
            <img
              src={attestation.signature_data}
              alt="Signature"
              className="max-h-24 mx-auto"
            />
          ) : (
            <p className="text-center text-xl font-signature text-gray-700 italic">
              {attestation.signature_data || attestation.attested_by_name}
            </p>
          )}
          <p className="text-xs text-center text-gray-500 mt-2">
            {attestation.signature_type === 'drawn' ? 'Hand-drawn signature' : 'Typed signature'}
          </p>
        </div>
      </div>

      {/* Previous Review Notes (if any) */}
      {attestation.review_notes && (
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-6">
          <h2 className="text-lg font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Previous Review Notes
          </h2>
          <p className="text-amber-700">{attestation.review_notes}</p>
          {attestation.reviewed_at && (
            <p className="text-sm text-amber-600 mt-2">
              {new Date(attestation.reviewed_at).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <AttestationReviewActions
        onApprove={() => openActionModal('approve')}
        onReject={() => openActionModal('reject')}
        onRequestChanges={() => openActionModal('changes')}
        isLoading={actionInProgress}
      />

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {showConfirmModal === 'approve' && 'Approve Attestation'}
              {showConfirmModal === 'reject' && 'Reject Attestation'}
              {showConfirmModal === 'changes' && 'Request Changes'}
            </h3>
            <p className="text-gray-600 mb-4">
              {showConfirmModal === 'approve' && 'This will activate the attestation and enable regulated features for the tenant.'}
              {showConfirmModal === 'reject' && 'This will permanently reject the attestation. The tenant will need to submit a new one.'}
              {showConfirmModal === 'changes' && 'The tenant will be notified to update their attestation.'}
            </p>

            {(showConfirmModal === 'reject' || showConfirmModal === 'changes') && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {showConfirmModal === 'reject' ? 'Rejection Reason *' : 'Change Request Notes *'}
                </label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-personic-500"
                  rows={3}
                  placeholder={showConfirmModal === 'reject' ? 'Explain why this attestation is being rejected...' : 'Describe what changes are needed...'}
                  required
                />
              </div>
            )}

            {showConfirmModal === 'approve' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-personic-500"
                  rows={2}
                  placeholder="Any notes about this approval..."
                />
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowConfirmModal(null)
                  setActionNotes('')
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={actionInProgress}
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction(showConfirmModal)}
                disabled={
                  actionInProgress ||
                  ((showConfirmModal === 'reject' || showConfirmModal === 'changes') && !actionNotes.trim())
                }
                className={`px-4 py-2 rounded-lg font-medium disabled:opacity-50 ${
                  showConfirmModal === 'approve'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : showConfirmModal === 'reject'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-amber-600 text-white hover:bg-amber-700'
                }`}
              >
                {actionInProgress ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
